import { Color3 } from '@babylonjs/core';
import type { VisualSettings } from './Settings.js';

/**
 * Two color presets the player can swap between in Settings.
 *
 * - PASTEL (default): the cartoon look introduced by the visual overhaul —
 *   near-white cyan horizon, light pastel greens.
 * - CLASSIC: the pre-overhaul master palette — saturated blue sky, darker
 *   mossy greens. Restored as an opt-in so testers can A/B against the new style.
 *
 * Read once at scene/chunk creation, so toggling the setting requires a page reload.
 * Mirrors the `DIAGNOSTIC` pattern: a single mutable global initialized once at
 * startup from settings, then read by leaf consumers (Scene, meshes, trees) without
 * each having to receive `settings` as an argument.
 */
export interface Palette {
  /** Sky/clear/fog horizon. Shared between scene.clearColor and scene.fogColor. */
  horizon: Color3;
  /** Scene-wide ambient color used by all unlit shading paths. */
  ambient: Color3;
  /** HemisphericLight diffuse (sky tint coming from above). */
  hemiSky: Color3;
  /** HemisphericLight ground color (bounced light coming from below). */
  hemiGround: Color3;
  /** DirectionalLight (sun) intensity. */
  sunIntensity: number;
  /** Sun color. */
  sun: Color3;
  /** Fog start distance (meters). */
  fogStart: number;
  /** Fog end distance (meters). */
  fogEnd: number;
  /** Tree trunk diffuse. */
  trunk: Color3;
  /** Tree crown diffuse. */
  crown: Color3;
  /** Terrain flat-fallback green (used when TerrainShadingPlugin is disabled). */
  terrain: Color3;
}

const PASTEL: Palette = {
  horizon: new Color3(0.831, 0.937, 0.973),
  ambient: new Color3(0.4, 0.5, 0.45),
  hemiSky: new Color3(0.722, 0.894, 0.941),
  hemiGround: new Color3(0.498, 0.690, 0.412),
  sunIntensity: 1.15,
  sun: new Color3(1.0, 0.973, 0.878),
  // Same fog band as classic (600–1400 m) so the default fog-distance slider value
  // of 1.0× lands on 1400 m for both palettes.
  fogStart: 600,
  fogEnd: 1400,
  trunk: new Color3(0.42, 0.30, 0.22),
  crown: new Color3(0.46, 0.65, 0.42),
  terrain: new Color3(0.498, 0.690, 0.412),
};

const CLASSIC: Palette = {
  horizon: new Color3(0.62, 0.78, 0.92),
  ambient: new Color3(0.4, 0.45, 0.5),
  hemiSky: new Color3(0.85, 0.9, 1.0),
  hemiGround: new Color3(0.25, 0.28, 0.2),
  sunIntensity: 1.1,
  sun: new Color3(1.0, 0.96, 0.88),
  fogStart: 600,
  fogEnd: 1400,
  trunk: new Color3(0.32, 0.22, 0.16),
  crown: new Color3(0.18, 0.42, 0.2),
  terrain: new Color3(0.32, 0.45, 0.22),
};

/** Active palette. Mutated by `applyPaletteFromSettings`; read by consumers. */
export let PALETTE: Palette = PASTEL;

export function applyPaletteFromSettings(settings: VisualSettings): void {
  PALETTE = settings.useClassicColors ? CLASSIC : PASTEL;
}
